import { useState, useEffect } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import PageWrapper from '../components/PageWrapper';
import { Mail, Phone, MapPin, Send, Check } from 'lucide-react';
import { useApp } from '../context/AppContext';
import SocialIcons from '../components/SocialIcons';
import { t } from '../data/translations';
import { contactApi } from '../api/contact';

export default function AboutPage() {
  usePageTitle("Qui sommes-nous — Lodiss", "Découvrez Lodiss, votre partenaire tunisien spécialisé en équipements de protection et vêtements professionnels certifiés.");

  const { lang } = useApp();
  const T = t[lang];

  const [contactForm, setContactForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await contactApi.sendMessage({
        name: contactForm.name,
        email: contactForm.email,
        subject: contactForm.subject,
        message: contactForm.message,
      });
      setSent(true);
    } catch (err) {
      console.error('Failed to send message:', err);
      // Optional: add error handling UI
    }
  };

  return (
    <PageWrapper>
    <main className="bg-white min-h-screen">
      {/* Hero */}
      <section className="border-b border-gray-100 py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <span className="text-lodiss-red text-xs font-semibold uppercase tracking-widest mb-3 block">LODISS</span>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-950 mb-4">{T.about.title}</h1>
            <p className="text-base text-gray-600 leading-relaxed">{T.about.desc}</p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-14 bg-gray-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl font-bold text-gray-950 mb-8">{T.about.valuesTitle}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {T.about.values.map((val, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-100 p-6 hover:shadow-sm transition-shadow">
                <div className="w-8 h-1 bg-lodiss-red rounded mb-4"></div>
                <h3 className="font-semibold text-gray-900 mb-2">{val.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{val.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl font-bold text-gray-950 mb-8">{T.about.contactTitle}</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* Info */}
            <div>
              <div className="space-y-4 mb-8">
                {[
                  { icon: <Mail size={16} className="text-lodiss-red" />, label: T.about.email },
                  { icon: <Phone size={16} className="text-lodiss-red" />, label: T.about.phone },
                  { icon: <MapPin size={16} className="text-lodiss-red" />, label: T.about.address },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl border border-gray-100">
                    <span className="mt-0.5 flex-shrink-0">{item.icon}</span>
                    <span className="text-sm text-gray-700">{item.label}</span>
                  </div>
                ))}
              </div>

              <div className="bg-gray-950 rounded-xl p-6">
                <p className="text-white font-semibold text-sm mb-1">
                  {lang === 'fr' ? 'Horaires d\'ouverture' : 'Business hours'}
                </p>
                <p className="text-gray-400 text-sm">
                  {lang === 'fr' ? 'Lundi – Vendredi : 8h30 – 18h00' : 'Monday – Friday: 8:30 AM – 6:00 PM'}
                </p>
                <p className="text-gray-500 text-xs mt-1">
                  {lang === 'fr' ? 'Fermé les jours fériés' : 'Closed on public holidays'}
                </p>
                <div className="mt-3">
                  <SocialIcons baseColor="#6b7280" size={18} />
                </div>
              </div>
            </div>

            {/* Contact form */}
            <div>
              {sent ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-8 flex flex-col items-center text-center">
                  <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mb-3">
                    <Check size={22} className="text-emerald-600" />
                  </div>
                  <p className="font-semibold text-gray-900 mb-1">
                    {lang === 'fr' ? 'Message envoyé !' : 'Message sent!'}
                  </p>
                  <p className="text-sm text-gray-500">
                    {lang === 'fr' ? 'Nous vous répondrons dans les meilleurs délais.' : 'We will get back to you as soon as possible.'}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {[
                    { key: 'name', label: lang === 'fr' ? 'Nom' : 'Name', type: 'text' },
                    { key: 'email', label: 'Email', type: 'email' },
                    { key: 'subject', label: lang === 'fr' ? 'Sujet' : 'Subject', type: 'text' },
                  ].map(f => (
                    <div key={f.key}>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">{f.label}</label>
                      <input
                        type={f.type}
                        value={contactForm[f.key as keyof typeof contactForm]}
                        onChange={e => setContactForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                        className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-lodiss-red focus:ring-1 focus:ring-lodiss-red/20 transition-colors"
                        required
                      />
                    </div>
                  ))}
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                      {lang === 'fr' ? 'Message' : 'Message'}
                    </label>
                    <textarea
                      value={contactForm.message}
                      onChange={e => setContactForm(prev => ({ ...prev, message: e.target.value }))}
                      rows={4}
                      className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-lodiss-red focus:ring-1 focus:ring-lodiss-red/20 transition-colors resize-none"
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    className="flex items-center justify-center gap-2 w-full py-3.5 bg-lodiss-red hover:bg-lodiss-dark text-white font-semibold rounded-lg transition-colors text-sm"
                  >
                    <Send size={14} />
                    {lang === 'fr' ? 'Envoyer le message' : 'Send message'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
    </PageWrapper>
  );
}
